import React, { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList, NavigationMenuContent, NavigationMenuTrigger } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-6">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-5 w-20 bg-muted/20 animate-pulse rounded" data-index={i} />)}
      </div>;
  }
  return <NavigationMenu className="hidden md:flex">
      <NavigationMenuList className="gap-1">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            {item.children && item.children.length > 0 ? <>
                <NavigationMenuTrigger className="bg-transparent text-foreground hover:bg-transparent hover:text-primary focus:bg-transparent focus:text-primary data-[state=open]:bg-transparent font-sans text-sm font-medium transition-colors">
                  {item.label}
                </NavigationMenuTrigger>
                <NavigationMenuContent>
                  <ul className="grid w-[200px] gap-2 p-4 bg-popover rounded-md border border-border">
                    {item.children.map((child, j) => <li key={child.id} data-index={j}>
                        <NavigationMenuLink asChild>
                          <Link to={child.href || "#"} className="block select-none space-y-1 rounded-md p-3 leading-none no-underline outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground text-sm">
                            {child.label}
                          </Link>
                        </NavigationMenuLink>
                      </li>)}
                  </ul>
                </NavigationMenuContent>
              </> : <NavigationMenuLink asChild>
                <Link to={item.href || "#"} className="group relative px-4 py-2 text-sm font-medium text-foreground transition-colors hover:text-primary focus:text-primary outline-none">
                  {item.label}
                  <span className="absolute bottom-0 left-1/2 h-[1px] w-0 bg-primary transition-all duration-300 ease-out group-hover:w-full group-hover:left-0" />
                </Link>
              </NavigationMenuLink>}
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer({
  onClose
}: {
  onClose: () => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-full bg-muted/20 animate-pulse rounded" data-index={i} />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6 p-6">
      {menuItems.map((item, i) => <div key={item.id} className="flex flex-col gap-2" data-index={i}>
          <Link to={item.href || "#"} className="text-lg font-medium text-foreground hover:text-primary transition-colors" onClick={onClose}>
            {item.label}
          </Link>
          {item.children && item.children.length > 0 && <div className="flex flex-col gap-2 pl-4 border-l border-border/50">
              {item.children.map((child, j) => <Link key={child.id} to={child.href || "#"} className="text-sm text-muted-foreground hover:text-primary transition-colors" onClick={onClose} data-index={j}>
                  {child.label}
                </Link>)}
            </div>}
        </div>)}
      <div className="mt-4">
        <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90" onClick={onClose}>
          Consult Now
        </Button>
      </div>
    </nav>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileMenuOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-300 border-b border-transparent dark bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-md border-border/40 shadow-sm" : "bg-background"}`}>
      <div className="container mx-auto px-4 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/" className="block">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1558991/img/inara_technologies_logo.png" alt="Inara Technologies" className="h-7 w-auto object-contain" />
          </Link>
        </div>

        {}
        <div className="hidden md:flex items-center gap-8">
          <MenuProvider menu_id="45" className="flex items-center">
            <DesktopMenuConsumer />
          </MenuProvider>

          {}
          <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-[0_0_15px_rgba(var(--primary),0.3)] animate-[pulse_3s_ease-in-out_infinite]">
            Consult Now
          </Button>
        </div>

        {}
        <div className="md:hidden">
          <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(true)} className="text-foreground hover:bg-accent hover:text-accent-foreground">
            <Menu className="h-6 w-6" />
            <span className="sr-only">Open menu</span>
          </Button>
        </div>
      </div>

      {}
      {isMobileMenuOpen && <div className="fixed inset-0 z-50 bg-background/95 backdrop-blur-md md:hidden animate-in fade-in slide-in-from-top-5 duration-200">
          <div className="container mx-auto px-4 h-20 flex items-center justify-between border-b border-border/40">
            <Link to="/" onClick={() => setIsMobileMenuOpen(false)}>
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1558991/img/inara_technologies_logo.png" alt="Inara Technologies" className="h-7 w-auto object-contain" />
            </Link>
            <Button variant="ghost" size="icon" onClick={() => setIsMobileMenuOpen(false)} className="text-foreground hover:bg-accent hover:text-accent-foreground">
              <X className="h-6 w-6" />
              <span className="sr-only">Close menu</span>
            </Button>
          </div>
          <div className="h-[calc(100vh-5rem)] overflow-y-auto">
            <MenuProvider menu_id="45">
              <MobileMenuConsumer onClose={() => setIsMobileMenuOpen(false)} />
            </MenuProvider>
          </div>
        </div>}
    </header>;
}